import type { ProjectBudgetMode } from '@inou/shared';
import { useState } from 'react';

import { Button } from '@/components/ui/button';
import { Field, FieldRow } from '@/components/ui/field';
import { NumberInput } from '@/components/ui/input';
import { Select } from '@/components/ui/select';
import { Table, TableWrap, Td, TdNum, Th } from '@/components/ui/table';
import { ApiError, api, type ProjectBudget, type ProjectBudgets } from '@/lib/api';
import { currentMonth, monthLabel, shiftMonth } from '@/lib/format';
import { useApi } from '@/lib/useApi';

/**
 * 入力欄の値を API に送る形へ。
 * 空欄は「未設定」なので null。0 は 0 のまま送る (予算 0 円は意味を持つ)。
 */
function amountOf(value: string): number | null {
  const trimmed = value.trim();
  if (trimmed === '') return null;
  const parsed = Number(trimmed);
  return Number.isFinite(parsed) ? Math.round(parsed) : null;
}

function textOf(value: number | null): string {
  return value === null ? '' : String(value);
}

/** 原価予算・受注金額の 2 入力。通期の 1 行にも月次の各行にも使う。 */
function AmountFields({
  budget,
  disabled,
  onSave,
}: {
  budget: ProjectBudget | undefined;
  disabled: boolean;
  onSave: (costBudget: number | null, revenueBudget: number | null) => void;
}) {
  const [cost, setCost] = useState(textOf(budget?.costBudget ?? null));
  const [revenue, setRevenue] = useState(textOf(budget?.revenueBudget ?? null));

  return (
    <FieldRow>
      <Field label="原価予算 (円)">
        <NumberInput
          value={cost}
          min={0}
          step={1}
          onChange={(e) => setCost(e.target.value)}
          disabled={disabled}
          className="w-40"
        />
      </Field>
      <Field label="受注金額 (円)">
        <NumberInput
          value={revenue}
          min={0}
          step={1}
          onChange={(e) => setRevenue(e.target.value)}
          disabled={disabled}
          className="w-40"
        />
      </Field>
      <Button
        variant="primary"
        disabled={disabled}
        onClick={() => onSave(amountOf(cost), amountOf(revenue))}
      >
        保存
      </Button>
    </FieldRow>
  );
}

/**
 * プロジェクトの予算。
 *
 * 金額だけを持ち、時間軸 (通期 / 月次) はプロジェクトごとに選ぶ。
 * モードを切り替えても反対側の行は消さないので、戻せば元の予算が復活する。
 *
 * 集計は行わない。ここは入力の口だけで、消化率や超過は
 * 収支・資産化レポート (`/api/reports/capitalization`) が出す。
 */
export function ProjectBudgetEditor({
  projectId,
  isAdmin,
  onModeChanged,
}: {
  projectId: string;
  isAdmin: boolean;
  /** budgetMode を変えたとき。一覧側のプロジェクト行を作り直させる。 */
  onModeChanged: () => void;
}) {
  const { data, error, loading, reload } = useApi<ProjectBudgets>(
    `/api/projects/${projectId}/budgets`,
  );
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [newMonth, setNewMonth] = useState(currentMonth);

  async function run(action: () => Promise<unknown>, note: string) {
    setBusy(true);
    setMessage(null);
    try {
      await action();
      setMessage(note);
      reload();
    } catch (cause) {
      setMessage(cause instanceof ApiError ? cause.message : '通信に失敗しました');
    } finally {
      setBusy(false);
    }
  }

  function save(month: string | null, costBudget: number | null, revenueBudget: number | null) {
    const removing = costBudget === null && revenueBudget === null;
    return run(
      () => api.put(`/api/projects/${projectId}/budgets`, { month, costBudget, revenueBudget }),
      removing ? '予算を消しました' : '保存しました',
    );
  }

  // 再取得で画面を白くしない。保存直後の再読み込みで入力欄ごと消えると、
  // 完了メッセージまで一緒に流れてしまう。
  if (loading && data === null) {
    return <p className="text-xs text-ink-subtle">読み込み中…</p>;
  }
  if (error) {
    return <p className="text-sm text-alert">{error.message}</p>;
  }
  if (!data) return null;

  const mode = data.budgetMode;
  const disabled = !isAdmin || busy;
  const monthsInUse = new Set(data.monthly.map((b) => b.month));

  return (
    <div className="flex flex-col gap-4">
      <FieldRow>
        <Field
          label="予算の持ち方"
          hint="通期は契約総額のように月に割れないもの、月次は年度予算を組むもの。"
        >
          <Select
            value={mode}
            disabled={disabled}
            onChange={(e) =>
              run(async () => {
                await api.patch(`/api/projects/${projectId}`, {
                  budgetMode: e.target.value as ProjectBudgetMode,
                });
                onModeChanged();
              }, '予算の持ち方を変えました')
            }
          >
            <option value="none">予算なし</option>
            <option value="total">通期 1 本</option>
            <option value="monthly">月次</option>
          </Select>
        </Field>
      </FieldRow>

      {mode === 'none' ? (
        <p className="text-xs leading-relaxed text-ink-subtle">
          予算を設定すると、収支・資産化レポートに消化率と超過が出ます。
        </p>
      ) : null}

      {mode === 'total' ? (
        <div className="flex flex-col gap-3">
          <AmountFields
            // モードを行き来しても入力欄が前の値を抱えたままにならないよう、
            // 読み込んだ行を key にして作り直す。
            key={`total-${data.total?.costBudget}-${data.total?.revenueBudget}`}
            budget={data.total ?? undefined}
            disabled={disabled}
            onSave={(cost, revenue) => save(null, cost, revenue)}
          />
          <p className="text-xs leading-relaxed text-ink-subtle">
            通期の予算は、レポートでは全期間から表示中の月までの累計実績と比べます。
            両方を空欄にして保存すると予算を消せます。
          </p>
        </div>
      ) : null}

      {mode === 'monthly' ? (
        <div className="flex flex-col gap-3">
          {data.monthly.length === 0 ? (
            <p className="text-xs text-ink-subtle">まだ月次の予算がありません。</p>
          ) : (
            <div className="border border-line bg-surface-raised">
              <TableWrap>
                <Table>
                  <thead>
                    <tr>
                      <Th>MONTH</Th>
                      <Th className="text-right">COST BUDGET</Th>
                      <Th className="text-right">ORDER</Th>
                      <Th />
                    </tr>
                  </thead>
                  <tbody>
                    {data.monthly.map((budget) => (
                      <MonthlyRow
                        key={budget.month}
                        budget={budget}
                        disabled={disabled}
                        onSave={(cost, revenue) => save(budget.month, cost, revenue)}
                      />
                    ))}
                  </tbody>
                </Table>
              </TableWrap>
            </div>
          )}

          {isAdmin ? (
            <FieldRow>
              <Field label="月を追加">
                <div className="flex items-center">
                  <Button
                    aria-label="前の月"
                    disabled={busy}
                    onClick={() => setNewMonth(shiftMonth(newMonth, -1))}
                  >
                    ←
                  </Button>
                  <span className="border-y border-line-bold px-4 py-2 font-mono text-sm text-ink-secondary">
                    {monthLabel(newMonth)}
                  </span>
                  <Button
                    aria-label="次の月"
                    disabled={busy}
                    onClick={() => setNewMonth(shiftMonth(newMonth, 1))}
                  >
                    →
                  </Button>
                </div>
              </Field>
              <Button
                disabled={busy || monthsInUse.has(newMonth)}
                onClick={() => save(newMonth, 0, null)}
              >
                {monthsInUse.has(newMonth) ? '追加済み' : '追加'}
              </Button>
            </FieldRow>
          ) : null}

          <p className="text-xs leading-relaxed text-ink-subtle">
            月次の予算は、レポートではその月の実績とだけ比べます。
            両方を空欄にして保存すると、その月の行を消せます。
          </p>
        </div>
      ) : null}

      {message ? <p className="text-xs text-ink-muted">{message}</p> : null}
    </div>
  );
}

/** 月次の 1 行。金額は行ごとに保存する。 */
function MonthlyRow({
  budget,
  disabled,
  onSave,
}: {
  budget: ProjectBudget;
  disabled: boolean;
  onSave: (costBudget: number | null, revenueBudget: number | null) => void;
}) {
  const [cost, setCost] = useState(textOf(budget.costBudget));
  const [revenue, setRevenue] = useState(textOf(budget.revenueBudget));

  return (
    <tr>
      <Td className="font-mono text-ink-secondary">{monthLabel(budget.month ?? '')}</Td>
      <TdNum>
        <NumberInput
          value={cost}
          min={0}
          step={1}
          onChange={(e) => setCost(e.target.value)}
          disabled={disabled}
          className="w-36 text-right"
        />
      </TdNum>
      <TdNum>
        <NumberInput
          value={revenue}
          min={0}
          step={1}
          onChange={(e) => setRevenue(e.target.value)}
          disabled={disabled}
          className="w-36 text-right"
        />
      </TdNum>
      <Td className="text-right">
        <Button disabled={disabled} onClick={() => onSave(amountOf(cost), amountOf(revenue))}>
          保存
        </Button>
      </Td>
    </tr>
  );
}
